<script>
export default {
  name: 'OrderResults'
}
</script>

<script setup>
import { ref, computed, nextTick } from 'vue';

import Pagination from '@parts/Pagination.vue';
import OrderDataUpdateDateDisplay from '@parts/OrderDataUpdateDateDisplay.vue';
import OrderListItem from '@parts/OrderListItem.vue';
import IdenticalFormulationModal from '@parts/IdenticalFormulationModal.vue';
import DatePicker from '@parts/DatePicker.vue';
import { dateUtil } from '@functions/dateUtil.js';
import { textUtil } from '@functions/textUtil.js';
import axios from 'axios';
import { modalUtil } from '@functions/modalUtil.js'
import SearchNumOverModal from '@parts/modal/SearchNumOverModal.vue'
import { url } from '@/config/urls';
import router from '@/router/index.js';
import { useAuthStore } from '@stores/auth.js';
import { message } from '@functions/message.js';

const { showModal } = modalUtil();
const authStore = useAuthStore();

// 定数定義
const pageSizeArray = [50, 100, 200];

// Prop定義
const props = defineProps({
  orderListApiUrl: {
    type: String,
    default: url.api.orderList
  },
  goodsTypesApiUrl: {
    type: String,
    default: url.api.goodsTypes
  },
  deliveryStatusesApiUrl: {
    type: String,
    default: url.api.deliveryStatuses
  },
  errorUrl: {
    type: String,
    default: url.error
  },
  loadingIcon: {
    type: String,
    default: url.static.loadingIcon
  },
  userDataApiUrl: {
    type: String,
    default: url.api.userData
  }
});

// 外部定義メソッド
const { comparisonDateString, checkDateFormat, examValidDate } = dateUtil();
const { convertHalfKanaToFullKana } = textUtil();

// 注文一覧取得APIリクエストパラメータ
const getOrderListCondition = ref({
  start: null,
  end: null,
  word: null,
  type: null,
  unissued: null,
  status: null,
  size: 50,
  page: 1
});

// フォーム入力値
const formInputs = ref({
  startChitDate: '',
  endChitDate: '',
  searchIndex: '',
  unissued: '',
  deliveryStatus: '',
  goodsType: ''
});

// リクエスト用日付データ
const requestDate = ref({
  start: '',
  end: ''
});

// エラー
const errors = ref({
  chitDate: '',
  searchIndex: '',
  deliveryStatus: '',
  goodsType: ''
});

// ページネーション
const pagingParameter = ref({
  pageSize: 50,
  pageNum: 1,
  hasNext: false
});
const totalPage = ref(1);

// 商品分類マスタデータ
const goodsTypes = ref([]);

// 配送ステータスマスタデータ
const deliveryStatuses = ref([]);

// 配送ステータス_検索キーリスト
const deliveryStatusesSearchKeys = ref([]);

// 検索結果
const orderList = ref([]);
const totalNum = ref(0);

// 同種同効品検索用
const orderedData = ref({});

// フラッシュメッセージ表示
const isShowMessage = ref(false);
const isShowOrderMessage = ref(false);

// 検索アニメーション表示
const isLoading = ref(false);

// アクセス日
const accessDate = ref(null);

// レイアウト切替判定
const isSimpleLayout = ref(false);

const showFlashMessage = (target) => {
  if (target == "order"){
    isShowOrderMessage.value = true;
    getOrderList();
    setTimeout(() => {
      isShowOrderMessage.value = false;
    }, 3000);
  } else if(target == "comment") {
    isShowMessage.value = true;
    setTimeout(() => {
      isShowMessage.value = false;
    }, 3000);
  }
}


// 同種同効品モーダル_検索対象データ更新
const updateModalData = (target) => {
  orderedData.value = target;
}

// 配送ステータスセレクトボックス有効化制御
const isActiveDeliveryStatusBox = computed({
  get() {
    return formInputs.value.unissued === 'unissued';
  }
})

// ユーザーデータ取得処理
const getUserInfo = () => {
  axios
    .get(props.userDataApiUrl)
    .then((response) => {
      if (response.data.response.orderListItemLayout === "1") {
        isSimpleLayout.value = true;
      }
    })
    .catch((error) => {
      switch (error?.response?.status) {
        case 401:
          router.replace({ path: '/login' }) ;
          break;
        case 500:
          sessionStorage.setItem("errorHeading", "サーバーエラー。");
          sessionStorage.setItem(
            "errorBody",
            "サーバーで問題が発生しました。現在、サービス提供ができない可能性があります。しばらくしてから再度アクセスしてください。"
          );
          router.replace({ path: '/error' }) ;
          break;
        default:
          break;
      }
    });
};

// 商品分類マスタデータ取得処理
const getGoodsTypes = () => {
  axios.get(props.goodsTypesApiUrl)
    .then((response) => {
      goodsTypes.value = response.data.response.goodsTypes;
    })
    .catch((error) => {
      switch(error?.response?.status) {
        case 401:
          router.replace({ path: '/login' }) ;
          break;
        case 500:
          sessionStorage.setItem('errorHeading', 'サーバーエラー。');
          sessionStorage.setItem('errorBody', 'サーバーで問題が発生しました。現在、サービス提供ができない可能性があります。しばらくしてから再度アクセスしてください。');
          router.replace({ path: '/error' }) ;
          break;
        default: break;
      }
    });
}

// 配送ステータスマスタデータ取得処理
const getDeliveryStatuses = () => {
  axios.get(props.deliveryStatusesApiUrl)
    .then((response) => {
      deliveryStatuses.value = response.data.response.deliveryStatuses;
      deliveryStatuses.value.forEach((deliveryStatus) => {
        if (!deliveryStatusesSearchKeys.value.some(searchKey => searchKey.key === deliveryStatus.searchKey)) {
          deliveryStatusesSearchKeys.value.push({
            key: deliveryStatus.searchKey,
            name: deliveryStatus.searchName
          });
        }
      });
      deliveryStatusesSearchKeys.value.sort((a, b) => a.key - b.key);
      // unissuedのステータス（欠品中・手配中・キャンセル）を除外
      deliveryStatusesSearchKeys.value.length = 5;
    })
    .catch((error) => {
      switch(error?.response?.status) {
        case 401:
          router.replace({ path: '/login' }) ;
          break;
        case 500:
          sessionStorage.setItem('errorHeading', 'サーバーエラー。');
          sessionStorage.setItem('errorBody', 'サーバーで問題が発生しました。現在、サービス提供ができない可能性があります。しばらくしてから再度アクセスしてください。');
          router.replace({ path: '/error' }) ;
          break;
        default: break;
      }
    });
}

// 注文一覧取得処理
const getOrderList = () => {
  isLoading.value = true;

  axios.get(props.orderListApiUrl, { params:getOrderListCondition.value })
    .then((response) => {
      orderList.value = convertGoodsInfoToFullKana(response.data.response.orderList);
      totalNum.value = parseInt(response.data.response.totalNum);
      pagingParameter.value.pageNum = parseInt(response.data.response.currentPage);
      pagingParameter.value.hasNext = response.data.response.hasNext;
      totalPage.value = Math.ceil(totalNum.value / pagingParameter.value.pageSize);
      totalPage.value = totalPage.value ? totalPage.value : 1;
      if (totalNum.value > 40000) {
        orderList.value = [];
        nextTick(() => { 
          showModal('searchNumOverModal');
        });
        return;
      }
    })
    .catch((error) => {
      switch(error?.response?.status) {
        case 400:
          // ユーザー依存で発生し得ないため500と同じ挙動
          sessionStorage.setItem('errorHeading', 'サーバーエラー。');
          sessionStorage.setItem('errorBody', 'サーバーで問題が発生しました。現在、サービス提供ができない可能性があります。しばらくしてから再度アクセスしてください。');
          router.replace({ path: '/error' }) ;
          break;
        case 401:
          router.replace({ path: '/login' }) ;
          break;
        case 404:
          orderList.value = [];
          totalNum.value = 0;
          pagingParameter.value.pageNum = 1;
          pagingParameter.value.hasNext = false;
          totalPage.value = 1;
          break;
        case 500:
          sessionStorage.setItem('errorHeading', 'サーバーエラー。');
          sessionStorage.setItem('errorBody', 'サーバーで問題が発生しました。現在、サービス提供ができない可能性があります。しばらくしてから再度アクセスしてください。');
          router.replace({ path: '/error' }) ;
          break;
        default: break;
      }
    })
    .finally(() => {
      isLoading.value = false;
    });
}

// orderListの商品情報の半角カナを全角に変換
const convertGoodsInfoToFullKana = (orderList) => {
  return orderList.map(order => {
    const convertedOrderList = { ...order };
    if (convertedOrderList.formalGoodsName1) {
      convertedOrderList.formalGoodsName1 = convertHalfKanaToFullKana(convertedOrderList.formalGoodsName1);
    };
    if (convertedOrderList.formalGoodsName2) {
      convertedOrderList.formalGoodsName2 = convertHalfKanaToFullKana(convertedOrderList.formalGoodsName2);
    };
    if (convertedOrderList.reorderFormalGoodsName1) {
      convertedOrderList.reorderFormalGoodsName1 = convertHalfKanaToFullKana(convertedOrderList.reorderFormalGoodsName1);
    };
    if (convertedOrderList.reorderFormalGoodsName2) {
      convertedOrderList.reorderFormalGoodsName2 = convertHalfKanaToFullKana(convertedOrderList.reorderFormalGoodsName2);
    };
    return convertedOrderList;
  });
}

// 開始日入力欄Enterキー押下時の処理
const startChitDate = ref(null);
const onKeyDownEnterStartChitDate = (event) => {
  // Datepicker非表示
  startChitDate.value?.hide();

  onKeyDownEnterSearchIndex(event);
}

// 終了日入力欄Enterキー押下時の処理
const endChitDate = ref(null);
const onKeyDownEnterEndChitDate = (event) => {
  // Datepicker非表示
  endChitDate.value?.hide();

  onKeyDownEnterSearchIndex(event);
}

// 検索キーワード入力欄Enterキー押下時の処理
const onKeyDownEnterSearchIndex = (event) => {
  // 検索時フォーカスアウト(仮想キーボードの非表示)
  event.target.blur();

  clickSearchBtn();
}

// ボタンクリック
const clickSearchBtn = () => {
  dateValidation();
  searchIndexValidation();
  deliveryStatusesValidation();
  goodsTypesValidation();

  if (Object.values(errors.value).some(value => value !== '')) {
    return;
  }

  setCondition();
  getOrderList();
}

const clickClearBtn = () => {
  formInputs.value.startChitDate = accessDateString.value.replaceAll('-', '/');
  formInputs.value.endChitDate = '';
  formInputs.value.searchIndex = '';
  formInputs.value.unissued = 'all';
  formInputs.value.deliveryStatus = '';
  formInputs.value.goodsType = '';
}

// リロードボタン
const updateLatestList = () => {
  getOrderListCondition.value.page = 1;
  getOrderList();
  resetReloadedCondition();
}

// ページネーション処理
const onChangePageNum = (page) => {
  if (!Number.isFinite(page)) {
    return;
  }
  getOrderListCondition.value.page = page;
  getOrderList();

  pagingParameter.value.pageNum = page;

  window.scroll({
      top: 0,
      behavior: 'smooth'
  });
}

const onChangePageSize = (selected) => {
  getOrderListCondition.value.page = 1;
  getOrderListCondition.value.size = selected;
  getOrderList();

  pagingParameter.value.pageNum = 1;
  pagingParameter.value.pageSize = selected;

}

// 検索条件のセット
const setCondition = () => {
  getOrderListCondition.value.start = requestDate.value.start || null;
  getOrderListCondition.value.end = requestDate.value.end || null;
  getOrderListCondition.value.word = formInputs.value.searchIndex || null
  getOrderListCondition.value.type = formInputs.value.goodsType || null;
  getOrderListCondition.value.unissued = (formInputs.value.unissued === 'unissued') || null;
  getOrderListCondition.value.status =
      !getOrderListCondition.value.unissued && formInputs.value.deliveryStatus || null;
  getOrderListCondition.value.size = pagingParameter.value.pageSize || 50;
  getOrderListCondition.value.page = 1;
  pagingParameter.value.pageNum = 1;
}

// リロード時の検索条件の再設定
const resetReloadedCondition = () => {
  const startChitDate = getOrderListCondition.value.start || '';
  const endChitDate = getOrderListCondition.value.end || '';

  formInputs.value.startChitDate = startChitDate.replaceAll('-', '/');
  formInputs.value.endChitDate = endChitDate.replaceAll('-', '/');
  formInputs.value.searchIndex = getOrderListCondition.value.word || '';
  formInputs.value.goodsType = getOrderListCondition.value.type || '';
  formInputs.value.unissued = getOrderListCondition.value.unissued ? 'unissued' : 'all';
  formInputs.value.deliveryStatus = getOrderListCondition.value.status || '';
  pagingParameter.value.pageNum = 1;
}

// バリデーション処理
const dateValidation = () => {
  
  errors.value.chitDate = '';
  requestDate.value.start = '';
  requestDate.value.end = '';

  if (formInputs.value.startChitDate) {
    if (!checkDateFormat(formInputs.value.startChitDate)) {
      errors.value.chitDate = message.error.validation.date.format;
    }else if(!examValidDate(formInputs.value.startChitDate)){
      errors.value.chitDate = message.error.validation.date.validity;
    }
    requestDate.value.start = formInputs.value.startChitDate.replaceAll('/', '-');
  }
  if (formInputs.value.endChitDate){
    if (!checkDateFormat(formInputs.value.endChitDate)) {
      errors.value.chitDate = message.error.validation.date.format;
    }else if(!examValidDate(formInputs.value.endChitDate)){
      errors.value.chitDate = message.error.validation.date.validity;
    }
    requestDate.value.end = formInputs.value.endChitDate.replaceAll('/', '-');
  }
  if (!errors.value.chitDate && (requestDate.value.start && requestDate.value.end)) {
    if (comparisonDateString(requestDate.value.start, '>', requestDate.value.end)) {
      let errorMessage = message.error.validation.date.comparison;
      errorMessage = errorMessage.replace('{end}', '終了日');
      errorMessage = errorMessage.replace('{start}', '開始日');
      errors.value.chitDate = errorMessage;
    }
  }
}

const searchIndexValidation = () => {
  const maxLength = 255;
  errors.value.searchIndex = '';

  if (formInputs.value.searchIndex.length > maxLength) {
    errors.value.searchIndex = '255文字以内で入力してください。';
  }
}

const deliveryStatusesValidation = () => {
  if (formInputs.value.unissued) { return; }
  errors.value.deliveryStatus = '';
  if (!formInputs.value.deliveryStatus) { return; }
  if (!deliveryStatusesSearchKeys.value.some(searchKey => searchKey.key === formInputs.value.deliveryStatus)) {
    errors.value.deliveryStatus = '正しい状況を選択してください。';
  }
}

const goodsTypesValidation = () => {
  errors.value.goodsType = '';
  if (!formInputs.value.goodsType) { return; }
  if (!goodsTypes.value.some(goodsType => goodsType.code === formInputs.value.goodsType)) {
    errors.value.goodsType = '正しい商品分類を選択してください。';
  }
}

const accessDateString = computed({
  get() {
    return accessDate.value.getFullYear() + '-'
      + (accessDate.value.getMonth()+1).toString().padStart(2, '0') + '-'
      + accessDate.value.getDate().toString().padStart(2, '0');
  }
});

// 初期処理
(() => {
  // 初期検索条件追加
  accessDate.value = new Date();
  getOrderListCondition.value.start = accessDateString.value;
  formInputs.value.startChitDate = accessDateString.value.replaceAll('-', '/');

  getUserInfo();
  getGoodsTypes();
  getDeliveryStatuses();
  getOrderList();
})();
</script>

<template>
  <div id="com_top_message1" :class="{ show: isShowMessage }">コメントを保存しました</div>
  <div id="com_top_message1" :class="{ show: isShowOrderMessage }">発注しました</div>

  <div id="contents1">

    <div class="com_inner2 clearfix">

      <OrderDataUpdateDateDisplay
        @updateLatestList="updateLatestList"
      ></OrderDataUpdateDateDisplay>

    </div>

    <div class="com_result1">

      <div class="com_inner2">

      <!-- searchForm -->
      <h2 class="com_title1">注文結果</h2>

        <div class="datepicker-group1">
          <div>
            <label for="js-datepicker1">開始日</label>
            <DatePicker
              v-model="formInputs.startChitDate"
              identifier="js-datepicker1" @keydown.enter="onKeyDownEnterStartChitDate"
              enterkeyhint="search" ref="startChitDate"
            ></DatePicker>
          </div>
          <div>
            <label for="js-datepicker2">終了日</label>
            <DatePicker
              v-model="formInputs.endChitDate"
              identifier="js-datepicker2" @keydown.enter="onKeyDownEnterEndChitDate"
              enterkeyhint="search" ref="endChitDate"
            ></DatePicker>
          </div>
        </div>
        <div class="com_validate_msg1 st1 error" v-if="errors.chitDate">{{ errors.chitDate }}</div>

        <div class="group1">
          <div>
            <input type="text" name="" placeholder="メーカー名、商品名、商品コード、GTIN"
                   class="form-control com_form1" v-model="formInputs.searchIndex"
                   :class="{ error: errors.searchIndex }"
                   @keydown.enter="onKeyDownEnterSearchIndex" enterkeyhint="search">
            <div class="com_validate_msg1 error" v-if="errors.searchIndex">{{ errors.searchIndex }}</div>
          </div>
          <div>
            <div class="com_btn_toggle_group1">
              <div>
                <input type="radio" name="options" value="all" id="option1"
                       v-model="formInputs.unissued" @keydown.enter="clickSearchBtn" checked>
                <label class="btn">全て</label>
              </div>
              <div>
                <input type="radio" name="options" value="unissued" id="option2"
                       v-model="formInputs.unissued" @keydown.enter="clickSearchBtn">
                <label class="btn">未出庫</label>
              </div>
            </div>
            <div class="buttons1">
              <button type="button" class="btn com_btn1" @click.prevent="clickSearchBtn">検索</button>
              <button type="button" class="btn com_btn2" @click.prevent="clickClearBtn">クリア</button>
            </div>
          </div>
        </div>

        <div class="group2">
          <div>
            <select class="form-select com_form1" v-model="formInputs.goodsType" :class="{ error: errors.goodsType }">
              <option value="" selected>商品分類:すべて</option>
              <option v-for="goodsType in goodsTypes" :value="goodsType.code">
                {{ goodsType.name }}
              </option>
            </select>
            <div class="com_validate_msg1 error" v-if="errors.goodsType">{{ errors.goodsType }}</div>
          </div>
          <div>
            <select class="form-select com_form1" v-model="formInputs.deliveryStatus" :class="{ error: errors.deliveryStatus }" :disabled="isActiveDeliveryStatusBox">
              <option value="" selected>状況:すべて</option>
              <option v-for="searchKey in deliveryStatusesSearchKeys" :value="searchKey.key">
                {{ searchKey.name }}
              </option>
            </select>
            <div class="com_validate_msg1 error" v-if="errors.deliveryStatus">{{ errors.deliveryStatus }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="com_inner2 clearfix">

      <template v-if="orderList.length === 0">
        <p class="com_fs16 text-center">ご注文履歴はありません。</p>
      </template>
      <template v-else>
        <p class="com_fs16 text-center order-results-text">検索結果は以下の通りです</p>

        <Pagination
          :isShowFirstAndLastBtn="true"
          :isShowPaginationInfo="true"
          :totalNum="totalNum"
          :currentPage="pagingParameter.pageNum"
          :totalPage="totalPage"
          :pageSize="pagingParameter.pageSize"
          :hasNext="pagingParameter.hasNext"
          :pageSizeSelectList="pageSizeArray"
          @changePageNum="onChangePageNum"
          @changePageSize="onChangePageSize"
        ></Pagination>

        <template v-for="(orderListItem, index) in orderList">
        <OrderListItem
          :class = "['order-rasults_parent']"
          :order = orderListItem
          :deliveryStatuses = deliveryStatuses
          :goodsTypes = goodsTypes
          :index = index
          :isSimpleLayout = isSimpleLayout
          @showFlashMessage="showFlashMessage"
          @updateModalData="updateModalData"
        ></OrderListItem>
        </template>

        <Pagination
          :isShowFirstAndLastBtn="true"
          :isShowPaginationInfo="true"
          :totalNum="totalNum"
          :currentPage="pagingParameter.pageNum"
          :totalPage="totalPage"
          :pageSize="pagingParameter.pageSize"
          :hasNext="pagingParameter.hasNext"
          :pageSizeSelectList="pageSizeArray"
          @changePageNum="onChangePageNum"
          @changePageSize="onChangePageSize"
        ></Pagination>

      </template>

    </div>
  </div>

  <IdenticalFormulationModal
    :ordered="orderedData"
    @showFlashMessage="showFlashMessage"
  ></IdenticalFormulationModal>

  <SearchNumOverModal
    :total-num="totalNum"
  ></SearchNumOverModal>

  <Transition>
    <div class="loading-animation" v-show="isLoading">
      <div class="show-icon-box">
        <img :src="props.loadingIcon" />
      </div>
    </div>
  </Transition>

</template>
